import moment from 'utils/moment';
import type { IBlockHourResItem } from 'services/types/blockHours';
import type { ITimeOffEntry } from './types';

// List responses carry `startTime`/`endTime` as `MM-DD-YYYY hh:mm A` (the date
// parts give the entry's day range) plus the BE explicit time fields
// `allDay`/`fromMin`/`toMin`. Parse the datetimes with an explicit format.
const LIST_DT_FORMAT = 'MM-DD-YYYY hh:mm A';

type DaySchedule = { startTime?: string; endTime?: string; enable?: boolean } | undefined;
export type ScheduleMap = Record<string, DaySchedule> | null | undefined;

const ALL_DAY_TOLERANCE_MIN = 15;
const MINUTES_IN_DAY = 24 * 60;

const toMinutes = (hhmm: string): number => {
  const m = moment(hhmm, 'HH:mm');
  return m.isValid() ? m.hour() * 60 + m.minute() : NaN;
};

/**
 * Legacy fallback for `allDay` when a record predates the BE explicit field: an
 * entry reads as all-day when its window covers the staff's full working window
 * for that weekday (±15min), or spans effectively midnight→midnight. Records
 * created after the BE change carry `allDay` and skip this path.
 */
const deriveAllDay = (fromMin: number, toMin: number, startDate: string, schedule: ScheduleMap): boolean => {
  // Spans (almost) the whole calendar day.
  if (fromMin <= ALL_DAY_TOLERANCE_MIN && toMin >= MINUTES_IN_DAY - 1 - ALL_DAY_TOLERANCE_MIN) return true;

  const weekday = moment(startDate, 'YYYY-MM-DD').format('ddd'); // 'Mon'...'Sun'
  const day = schedule?.[weekday];
  if (!day?.startTime || !day?.endTime) return false;

  const workStart = toMinutes(day.startTime);
  const workEnd = toMinutes(day.endTime);
  if (Number.isNaN(workStart) || Number.isNaN(workEnd)) return false;

  return Math.abs(fromMin - workStart) <= ALL_DAY_TOLERANCE_MIN && Math.abs(toMin - workEnd) <= ALL_DAY_TOLERANCE_MIN;
};

export interface AdaptContext {
  staffName?: string;
  schedule?: ScheduleMap;
}

/** Raw block-hours API item → normalized time-off entry. Pure function. */
export const adaptBlockHourToTimeOff = (item: IBlockHourResItem, ctx: AdaptContext = {}): ITimeOffEntry => {
  const startM = moment(item.startTime, LIST_DT_FORMAT);
  const endM = moment(item.endTime, LIST_DT_FORMAT);
  const startValid = startM.isValid();
  const endValid = endM.isValid();

  const start = startValid ? startM.format('YYYY-MM-DD') : '';
  const end = endValid ? endM.format('YYYY-MM-DD') : start;

  // Time-of-day parsed from the datetimes (legacy fallback). For multi-day timed
  // records BE may encode the full span here, so the explicit fields win.
  const parsedFrom = startValid ? startM.hour() * 60 + startM.minute() : 0;
  const parsedTo = endValid ? endM.hour() * 60 + endM.minute() : 0;

  // Prefer BE explicit fields; derive/parse only for records that predate them.
  const allDay = typeof item.allDay === 'boolean' ? item.allDay : deriveAllDay(parsedFrom, parsedTo, start, ctx.schedule);
  const fromMin = typeof item.fromMin === 'number' ? item.fromMin : parsedFrom;
  const toMin = typeof item.toMin === 'number' ? item.toMin : parsedTo;

  return {
    id: item.id,
    staffId: item.staffId,
    name: ctx.staffName || String(item.staffId),
    start,
    end,
    allDay,
    fromMin: allDay ? undefined : fromMin,
    toMin: allDay ? undefined : toMin,
    reason: item.reason || '',
  };
};
